import { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";

const WHATSAPP_NUMBER = "14086586805";
const WHATSAPP_PREFILL = "Hi Majura, I'd like to ask about your platform.";

const CONTACT_LAMBDA_URL = process.env.REACT_APP_CONTACT_LAMBDA_URL || "";

const MessageIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 5.5A2.5 2.5 0 016.5 3h11A2.5 2.5 0 0120 5.5v8A2.5 2.5 0 0117.5 16H10l-4.6 3.45A.6.6 0 014.5 19v-3H6.5A2.5 2.5 0 004 13.5v-8z"
      fill="currentColor"
    />
    <circle cx="8.2" cy="9.4" r="1.15" fill="#fff" />
    <circle cx="12" cy="9.4" r="1.15" fill="#fff" />
    <circle cx="15.8" cy="9.4" r="1.15" fill="#fff" />
  </svg>
);

const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="M4 6.5l8 6.5 8-6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3a9 9 0 00-7.75 13.5L3 21l4.65-1.22A9 9 0 1012 3z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M8.5 8.7c.2-.5.5-.5.8-.5h.5c.2 0 .4 0 .55.4.2.5.6 1.5.65 1.6.05.15.1.3 0 .5-.1.2-.15.3-.3.45-.15.15-.3.3-.15.55.15.3.7 1.15 1.5 1.85.9.8 1.55 1 1.8 1.15.25.15.4.15.55-.05.15-.2.6-.7.75-.95.15-.25.3-.2.5-.1.2.1 1.3.6 1.5.7.2.1.35.15.4.25.05.15.05.85-.25 1.35-.3.5-1.3.95-1.85 1-.5.1-1.1.15-2.85-.6-2.4-1.05-3.95-3.4-4.1-3.55-.15-.15-.95-1.25-.95-2.4 0-1.15.6-1.7.8-1.95z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type Mode = "choose" | "email";

const ContactWidget = () => {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [mode, setMode] = useState<Mode>("choose");
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 8000);
    return () => clearTimeout(timer);
  }, [success]);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
    setHasOpened(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_PREFILL)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const { name, email, message } = formData;
    if (!name || !email || !message) {
      setError("Please fill in all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Invalid email address.");
      return;
    }
    if (!CONTACT_LAMBDA_URL) {
      setError("The contact form isn't wired up yet — try WhatsApp instead.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(CONTACT_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Something went wrong.");

      setSuccess(data.message || "Message sent — we'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LauncherWrap>
        <Tooltip $idle={!hasOpened && !open}>
          Send us a message to enquire about us or chat with us
        </Tooltip>
        <LauncherButton
          onClick={toggleOpen}
          $idle={!hasOpened && !open}
          title="Send us a message to enquire about us or chat with us"
          aria-label="Contact Majura"
        >
          {open ? <CloseIcon /> : <MessageIcon />}
        </LauncherButton>
      </LauncherWrap>

      {open && (
        <Panel role="dialog" aria-label="Contact Majura">
          <PanelHeader>
            <HeaderIdentity>
              <HeaderIconWrap>
                <MessageIcon />
              </HeaderIconWrap>
              <HeaderTitle>Majura</HeaderTitle>
            </HeaderIdentity>
            <CloseButton onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </CloseButton>
          </PanelHeader>

          <PanelBody>
            {mode === "choose" && (
              <ChoiceList>
                <ChoiceCard onClick={() => setMode("email")}>
                  <ChoiceIcon $tone="email">
                    <MailIcon />
                  </ChoiceIcon>
                  <ChoiceText>
                    <ChoiceTitle>Send us an email</ChoiceTitle>
                    <ChoiceDescription>We'll get back to you within a business day.</ChoiceDescription>
                  </ChoiceText>
                </ChoiceCard>

                <ChoiceCard onClick={handleWhatsApp}>
                  <ChoiceIcon $tone="whatsapp">
                    <WhatsAppIcon />
                  </ChoiceIcon>
                  <ChoiceText>
                    <ChoiceTitle>Chat on WhatsApp</ChoiceTitle>
                    <ChoiceDescription>Message us directly for a faster reply.</ChoiceDescription>
                  </ChoiceText>
                </ChoiceCard>
              </ChoiceList>
            )}

            {mode === "email" && (
              <EmailForm onSubmit={handleEmailSubmit}>
                <BackRow type="button" onClick={() => setMode("choose")}>
                  <BackIcon /> Back
                </BackRow>

                <Label>Name</Label>
                <Input
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                />

                <Label>Email</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />

                <Label>Message</Label>
                <TextArea
                  name="message"
                  placeholder="How can we help?"
                  value={formData.message}
                  onChange={handleChange}
                />

                <SubmitButton type="submit" disabled={loading}>
                  {loading ? "Sending…" : "Send message"}
                </SubmitButton>

                {error && <StatusText $tone="error">{error}</StatusText>}
                {success && <StatusText $tone="success">{success}</StatusText>}
              </EmailForm>
            )}
          </PanelBody>
        </Panel>
      )}
    </>
  );
};

const wiggle = keyframes`
  0%, 82%, 100% { transform: rotate(0deg); }
  84% { transform: rotate(-9deg); }
  87% { transform: rotate(8deg); }
  90% { transform: rotate(-6deg); }
  93% { transform: rotate(4deg); }
  96% { transform: rotate(0deg); }
`;

const peek = keyframes`
  0%, 80% { opacity: 0; transform: translateY(6px) scale(0.96); }
  86%, 95% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(6px) scale(0.96); }
`;

const LauncherWrap = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 60;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: 640px) {
    right: 16px;
    bottom: 16px;
  }
`;

const Tooltip = styled.div<{ $idle: boolean }>`
  max-width: 220px;
  padding: 10px 14px;
  border-radius: 14px;
  background: #ffffff;
  color: #0b1c75;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.35;
  box-shadow: 0 12px 28px rgba(11, 28, 117, 0.18);
  opacity: 0;
  transform-origin: bottom right;
  ${({ $idle }) =>
    $idle &&
    css`
      animation: ${peek} 9s ease-in-out infinite;
    `}

  @media (max-width: 640px) {
    display: none;
  }
`;

const LauncherButton = styled.button<{ $idle: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(150deg, #4c5cf0 0%, #2f6bf6 100%);
  color: #ffffff;
  box-shadow: 0 10px 30px rgba(47, 107, 246, 0.45);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  ${({ $idle }) =>
    $idle &&
    css`
      animation: ${wiggle} 9s ease-in-out infinite;
    `}

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 14px 34px rgba(47, 107, 246, 0.55);
  }
`;

const Panel = styled.div`
  position: fixed;
  right: 24px;
  bottom: 96px;
  z-index: 60;
  width: min(360px, calc(100vw - 32px));
  border-radius: 20px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(11, 28, 117, 0.28);
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) {
    right: 16px;
    bottom: 88px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(120deg, #2f6bf6 0%, #4c5cf0 60%, #6f7dff 100%);
`;

const HeaderIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderIconWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const HeaderTitle = styled.span`
  color: #ffffff;
  font-weight: 700;
  font-size: 1.05rem;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.85;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const PanelBody = styled.div`
  padding: 18px;
`;

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChoiceCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid #e4eaf6;
  background: #f7f9ff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.15s ease;

  &:hover {
    border-color: #b7c2ff;
    background: #eef1ff;
    transform: translateY(-1px);
  }
`;

const ChoiceIcon = styled.span<{ $tone: "email" | "whatsapp" }>`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tone }) => ($tone === "whatsapp" ? "#25d366" : "#4c5cf0")};
  background: ${({ $tone }) => ($tone === "whatsapp" ? "#25d3661f" : "#4c5cf01f")};
`;

const ChoiceText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChoiceTitle = styled.span`
  font-weight: 700;
  font-size: 0.95rem;
  color: #0b1c75;
`;

const ChoiceDescription = styled.span`
  font-size: 0.8rem;
  color: #5a6786;
  line-height: 1.4;
`;

const EmailForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BackRow = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  border: none;
  background: transparent;
  color: #4c5cf0;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #0b1c75;
  margin-top: 8px;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid #d9e0f0;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid #d9e0f0;
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  min-height: 84px;
  resize: none;
`;

const SubmitButton = styled.button`
  margin-top: 12px;
  background: linear-gradient(90deg, #4c5cf0, #2f6bf6);
  color: #ffffff;
  border: none;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusText = styled.p<{ $tone: "error" | "success" }>`
  margin: 8px 0 0;
  font-size: 0.82rem;
  color: ${({ $tone }) => ($tone === "error" ? "#d0342c" : "#1f9d55")};
`;

export default ContactWidget;
